import { useEffect, useState } from 'react';
import api from '../api';
import { useMyMeasuring, refreshMine, loadDefinitions, loadActive, saveActive, syncActive, palletsOf, sendFinished, queueFinished, dropQueued, flushOutbox, stillOnItAfter } from './store';
import {
  TEAL, AMBER, GREEN, DIM, MUTED, BG, CARD, LINE, INK, UNDO_MS, nowIso, clock, plural, cap, secs, def,
  Head, Def, Box, Clock, Actions, back, lab, hint, bigInput, wide, quiet, stepsBox, stepRow, nextStep, num, stepper, sect, item, ic, smallBtn,
} from './recorderUi';
import { AssemblyStart, RoutineStart, BatchRun, LapRun, AttentionRun } from './LapRecorders';
import PhoneSetup from './PhoneSetup';

// The recorder: full screen on a phone, a panel on a desktop (the measuring
// tool, 7 Oct 2026). Big buttons for gloved hands, the agreed definition of
// every moment on screen, Undo for ten seconds, Abandon that asks first, and
// the result straight away against what the app had assumed. It never blocks
// production: closing it leaves a running recording running.

const STEPS = {
  mould_change: (t) => (t.mould_off_id ? ['start', 'off', 'on', 'good'] : ['start', 'on', 'good']),
  warmup: () => ['on', 'stable'],
  purge: () => ['start', 'clean'],
  cleaning: () => ['start', 'done'],
};
const ICON = { mould_change: 'fa-right-left', cycle: 'fa-stopwatch', warmup: 'fa-temperature-half', packing: 'fa-box', purge: 'fa-droplet', cleaning: 'fa-broom', attention: 'fa-person', assembly: 'fa-screwdriver-wrench', routine: 'fa-list-ol' };
// The line under a title says what the title does not: which moulds, or where.
function subOf(t) {
  if (t.source === 'asked') return t.why;
  if (t.kind === 'mould_change') return t.mould_off ? `${t.mould_off} → ${t.mould || 'new mould'}` : `${t.mould || 'the mould'} goes on`;
  if (t.kind === 'warmup') return t.mould || t.part || '';
  if (t.kind === 'purge') return t.assumed && t.assumed.from ? `${t.assumed.from} → ${t.assumed.to}` : '';
  if (t.kind === 'assembly') return t.method === 'steps' ? 'Step by step' : 'A batch';
  return t.machine || '';
}
const today = () => new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

export default function Recorder({ open, taskId, onClose }) {
  const mine = useMyMeasuring();
  const [defs, setDefs] = useState(null);
  const [active, setActive] = useState(loadActive);
  const [screen, setScreen] = useState({ name: 'list' });
  const [, tick] = useState(0);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmAbandon, setConfirmAbandon] = useState(false);
  const [reading, setReading] = useState('');
  const [scrap, setScrap] = useState('');
  const [onPallet, setOnPallet] = useState(0);

  useEffect(() => { if (open) { loadDefinitions().then(setDefs); refreshMine(); flushOutbox(); } }, [open]);
  // Open at a measurement (from the Today card), or where the running one is.
  useEffect(() => {
    if (!open) return;
    const a = loadActive();
    setActive(a);
    if (taskId) setScreen({ name: 'task', id: taskId });
    else if (a) setScreen({ name: 'task', id: a.taskId });
    else setScreen({ name: 'list' });
    setError(''); setConfirmAbandon(false);
  }, [open, taskId]);
  // The clock ticks from the saved start; nothing has to keep running.
  useEffect(() => {
    if (!open || !active) return undefined;
    const iv = setInterval(() => tick(n => n + 1), 1000);
    return () => clearInterval(iv);
  }, [open, active]);
  // Taps not yet sent go when the signal is back.
  useEffect(() => {
    if (!active || active.synced) return undefined;
    const iv = setInterval(async () => { const a = await syncActive(loadActive()); if (a) { saveActive(a); setActive(a); } }, 15000);
    return () => clearInterval(iv);
  }, [active]);

  if (!open) return null;

  const tasks = mine.tasks || [];
  const outbox = mine.outbox || [];
  const task = screen.id ? tasks.find(t => t.id === screen.id) : null;
  const queued = (t) => outbox.find(x => x.taskId === t.id);
  // A recording the server holds for me but this phone has forgotten (another
  // phone, cleared storage): pick it up from the server.
  const serverRunning = task && !active && (mine.running || []).find(m => m.task_id === task.id);

  async function persist(a) {
    saveActive(a); setActive(a);
    const synced = await syncActive(a);
    if (loadActive() && loadActive().startedAt === a.startedAt) { saveActive({ ...synced, stopped: loadActive().stopped }); setActive({ ...synced, stopped: loadActive().stopped }); }
  }
  function begin(t, first, fields = {}) {
    const a = { taskId: t.id, kind: t.kind, startedAt: nowIso(), marks: first ? [{ step: first, at: nowIso(), ...fields }] : [],
                onPallet: t.kind === 'packing' ? onPallet : 0, id: null, synced: false, title: t.title };
    setError(''); persist(a);
  }
  function adopt(m) {
    persist({ taskId: m.task_id, kind: m.kind, startedAt: m.started_at, marks: m.marks || [], onPallet: 0, id: m.id, synced: true, title: task.title });
  }
  function tap(step, fields = {}) { if (!active) return; persist({ ...active, marks: [...active.marks, { step, at: nowIso(), ...fields }], synced: false }); }
  function undo() {
    if (!active || active.marks.length < 2) return;
    const last = active.marks[active.marks.length - 1];
    if (Date.now() - Date.parse(last.at) > UNDO_MS) return;
    persist({ ...active, marks: active.marks.slice(0, -1), synced: false });
  }
  // Send a finished recording. With no signal it waits on this phone and goes
  // by itself; a refusal says why, and the recording stays open to carry on.
  async function send(item, onDone) {
    setBusy(true); setError('');
    const r = await sendFinished(item);
    if (r.ok) {
      onDone();
      setScreen(r.measurement ? { name: 'result', id: item.taskId, m: r.measurement } : { name: 'list' });
      refreshMine();
    } else if (r.offline) {
      queueFinished(item);
      onDone();
      setScreen({ name: 'saved', id: item.taskId });
    } else {
      setError(r.refused);
    }
    setBusy(false);
  }
  function finish(extra = {}, lastMark = null, at = null) {
    const a = loadActive() || active;
    if (!a) return undefined;
    const marks = lastMark ? [...a.marks, { step: lastMark, at: at || nowIso() }] : a.marks;
    return send({ taskId: a.taskId, id: a.id, startedAt: a.startedAt, title: a.title, kind: a.kind, body: { marks, on_pallet: a.onPallet, ...extra } },
      () => { saveActive(null); setActive(null); setScrap(''); });
  }
  function saveCycle(t) {
    const v = Number(String(reading).replace(',', '.'));
    if (!(v > 0)) { setError('Type the cycle time the machine shows, in seconds, for example 13.4.'); return undefined; }
    return send({ taskId: t.id, id: null, startedAt: nowIso(), title: t.title, kind: 'cycle', body: { reading: v, marks: [] } }, () => setReading(''));
  }
  async function abandon() {
    const a = loadActive() || active;
    setBusy(true);
    try { if (a && a.id) await api.post(`/api/v1/measuring/measurements/${a.id}/abandon`, { reason: 'Abandoned on the phone' }); } catch { /* thrown away here either way */ }
    saveActive(null); setActive(null); setConfirmAbandon(false); setBusy(false);
    setScreen({ name: 'list' }); refreshMine();
  }
  // What the assembly, routine and time-at-the-machine screens can do.
  const ctl = {
    busy, tap, undo,
    askAbandon: () => setConfirmAbandon(true),
    stop: () => persist({ ...active, stopped: nowIso() }),
    carryOn: () => persist({ ...active, stopped: null }),
    saveLaps: (v) => finish({ count: v.count, people: v.people, unit: v.unit, names: v.names, step_counts: v.step_counts }, 'finish', active.stopped),
    finishNow: () => finish({}, 'finish'),
  };

  // ── screens ──
  let body;
  if (screen.name === 'result') body = <Result t={task} m={screen.m} onDone={() => setScreen({ name: 'list' })} />;
  else if (screen.name === 'saved') {
    body = (
      <>
        <div style={{ textAlign: 'center', padding: '26px 16px 6px' }}>
          <div style={{ fontSize: 34, color: GREEN }}>✓</div>
          <div style={{ fontSize: 20, fontWeight: 800, marginTop: 4 }}>Saved on this phone</div>
          <div style={{ color: DIM, fontSize: 13, marginTop: 6 }}>There is no signal just now. It goes by itself when there is, and nothing more is needed from you. Thank you.</div>
        </div>
        <button onClick={() => setScreen({ name: 'list' })} style={wide}>Done</button>
      </>
    );
  } else if (screen.name === 'task' && task) {
    const running = active && active.taskId === task.id ? active : null;
    const otherRunning = active && active.taskId !== task.id;
    const d = (step) => def(defs, task.kind === 'mould_change' ? 'mould_change' : task.kind, step);
    const pickUp = !running && serverRunning
      ? <button onClick={() => adopt(serverRunning)} style={wide}>Carry on the recording started at {new Date(serverRunning.started_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</button>
      : null;
    const inPlay = task.status !== 'done' && !otherRunning && !queued(task);
    const after = running ? stillOnItAfter(task.kind, task.assumed) : null;
    body = (
      <>
        <button onClick={() => setScreen({ name: 'list' })} style={back}>‹ {running ? 'Back (it keeps running)' : 'Back'}</button>
        <Head k={task.machine || task.product || ''} h={task.title} s={task.why} />
        {otherRunning && <Box tone={AMBER}>Another recording is running: {active.title}. Finish or abandon it first.</Box>}
        {queued(task) && <Box tone={GREEN}>Saved on this phone. It goes by itself when there is signal.</Box>}
        {after && Date.now() - Date.parse(running.startedAt) > after * 60000 && (
          <Box tone={AMBER}>Still measuring? This has been running {clock(Date.now() - Date.parse(running.startedAt))}. Finish it, or Abandon it if it was forgotten: a clock left running would count as work.</Box>
        )}

        {task.status === 'done' && !running && <Box tone={GREEN}>Already measured. Thank you.</Box>}

        {/* Cycle time: one number */}
        {task.kind === 'cycle' && inPlay && (
          <>
            <Def d={d('reading')} />
            <div style={{ margin: '8px 14px' }}>
              <label htmlFor="rec-cycle" style={lab}>Cycle time on the display, in seconds</label>
              <input id="rec-cycle" inputMode="decimal" value={reading} onChange={e => setReading(e.target.value)}
                placeholder={task.assumed && task.assumed.cycle_s ? String(task.assumed.cycle_s) : '13.4'} style={bigInput} />
              {task.assumed && task.assumed.cycle_s && <div style={hint}>Planned at {task.assumed.cycle_s} s</div>}
            </div>
            <button disabled={busy} onClick={() => saveCycle(task)} style={wide}>Save</button>
          </>
        )}

        {/* Taps: mould change, warm-up, colour purge, cleaning */}
        {STEPS[task.kind] && inPlay && (() => {
          const steps = STEPS[task.kind](task);
          if (pickUp) return pickUp;
          if (!running) return <><Def d={d(steps[0])} /><button onClick={() => begin(task, steps[0])} style={wide}>{d(steps[0]).label}</button></>;
          const n = running.marks.length, doneAll = n >= steps.length;
          return (
            <>
              <Clock since={running.startedAt} />
              {!doneAll && <Def d={d(steps[n])} />}
              <div style={stepsBox}>
                {steps.map((st, i) => {
                  const mk = running.marks[i];
                  if (mk) return <div key={st} style={{ ...stepRow, color: DIM }}><span style={num}>{i + 1}</span>{d(st).label}<span style={{ marginLeft: 'auto', fontFamily: 'ui-monospace, monospace', color: GREEN }}>{i === 0 ? new Date(mk.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '+' + Math.round((Date.parse(mk.at) - Date.parse(running.marks[i - 1].at)) / 60000) + ' min'}</span></div>;
                  if (i === n) return <button key={st} onClick={() => tap(st)} style={nextStep}><span style={{ ...num, color: '#0b1215' }}>{i + 1}</span>{d(st).label}<span style={{ marginLeft: 'auto', fontSize: 12 }}>tap</span></button>;
                  return <div key={st} style={stepRow}><span style={num}>{i + 1}</span>{d(st).label}</div>;
                })}
              </div>
              {doneAll && task.kind === 'mould_change' && (
                <div style={{ margin: '4px 14px' }}>
                  <Def d={def(defs, 'mould_change', 'scrap')} />
                  <label htmlFor="rec-scrap" style={lab}>Start-up scrap (parts), if counted</label>
                  <input id="rec-scrap" inputMode="numeric" value={scrap} onChange={e => setScrap(e.target.value)} placeholder="optional" style={bigInput} />
                </div>
              )}
              {doneAll && <button disabled={busy} onClick={() => finish(task.kind === 'mould_change' ? { count: scrap } : {})} style={wide}>Finish</button>}
              <Actions running={running} onUndo={undo} onAbandon={() => setConfirmAbandon(true)} />
            </>
          );
        })()}

        {/* Packing: pallet-aware */}
        {task.kind === 'packing' && inPlay && (() => {
          const a = task.assumed || {}, unit = a.unit || 'box';
          if (pickUp) return pickUp;
          if (!running) return (
            <>
              <Def d={def(defs, 'packing', 'on_pallet')} />
              <div style={{ margin: '8px 14px' }}>
                <div style={lab}>{cap(plural(unit))} already on the pallet now</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <button aria-label="One fewer" onClick={() => setOnPallet(v => Math.max(0, v - 1))} style={stepper}>−</button>
                  <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 28, fontWeight: 700, minWidth: 40, textAlign: 'center' }}>{onPallet}</span>
                  <button aria-label="One more" onClick={() => setOnPallet(v => Math.min((a.palletOf || 99) - 1, v + 1))} style={stepper}>+</button>
                </div>
                <div style={hint}>So the app knows which {unit} closes the pallet: a pallet holds {a.palletOf || '?'} {plural(unit)} of {a.per || '?'}.</div>
              </div>
              <button onClick={() => begin(task, 'start')} style={wide}>Start: the next {unit} begins</button>
            </>
          );
          const P = palletsOf(running.marks, running.onPallet, a.palletOf), boxNo = P.on + 1;
          const lastPallet = P.done[P.done.length - 1];
          return (
            <>
              <Clock since={running.startedAt} />
              {P.closedLast && lastPallet && <Box tone={GREEN}>✓ Pallet {lastPallet.n} full. Pallet {P.cur.n} has started.</Box>}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 16px 6px', fontWeight: 700 }}>
                <span>Pallet {P.cur.n}{P.cur.partial ? ` · from ${unit} ${P.cur.startedAt + 1}` : ''}</span>
                <span style={{ fontFamily: 'ui-monospace, monospace', color: TEAL }}>{unit} {boxNo} of {a.palletOf || '?'}</span>
              </div>
              <div style={{ margin: '0 16px', height: 8, borderRadius: 4, background: '#12161b', border: `1px solid ${LINE}`, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${a.palletOf ? (P.on / a.palletOf) * 100 : 0}%`, background: TEAL }} />
              </div>
              <Def d={def(defs, 'packing', 'box')} />
              <div style={stepsBox}>
                {P.cur.boxes.length > 3 && <div style={hint}>{P.cur.boxes.length - 3} earlier {plural(unit)} on this pallet</div>}
                {P.cur.boxes.slice(-3).map(b => <div key={b.no} style={{ ...stepRow, color: DIM }}><span style={num}>{b.no}</span>{cap(unit)} {b.no}<span style={{ marginLeft: 'auto', fontFamily: 'ui-monospace, monospace', color: GREEN }}>{secs(b.secs)}</span></div>)}
                <button onClick={() => tap('box')} style={nextStep}>
                  <span style={{ ...num, color: '#0b1215' }}>{boxNo}</span>{boxNo === a.palletOf ? `${cap(unit)} ${boxNo} full · closes the pallet` : `${cap(unit)} ${boxNo} full (${a.per ? Number(a.per).toLocaleString('en-GB') : ''})`}<span style={{ marginLeft: 'auto', fontSize: 12 }}>tap</span>
                </button>
                {P.closedLast && (
                  <>
                    <Def d={def(defs, 'packing', 'pallet')} />
                    <button onClick={() => tap('pallet')} style={{ ...stepRow, borderStyle: 'dashed', color: INK, cursor: 'pointer', background: 'none' }}>
                      <span style={num}>✓</span>Pallet finished: wrapped, covered, labelled<span style={{ marginLeft: 'auto', fontSize: 12, color: DIM }}>optional</span>
                    </button>
                  </>
                )}
              </div>
              {running.marks.some(m => m.step === 'box') && <button disabled={busy} onClick={() => finish()} style={quiet}>Finish</button>}
              <Actions running={running} onUndo={undo} onAbandon={() => setConfirmAbandon(true)} />
              {P.done.length > 0 && (
                <>
                  <div style={sect}>Finished pallets</div>
                  {P.done.slice().reverse().map(p => (
                    <div key={p.n} style={{ ...item, opacity: 0.75 }}>
                      <span style={{ ...ic, background: 'rgba(245,158,11,0.15)', color: AMBER }}><i className="fa-solid fa-pallet" aria-hidden="true" /></span>
                      <span><div style={{ fontWeight: 700 }}>Pallet {p.n}{p.partial ? ' · part pallet' : ''}</div>
                        <div style={{ fontSize: 12, color: DIM }}>{p.boxes.length} {p.boxes.length === 1 ? unit : plural(unit)} · {Math.round((Date.parse(p.finishedAt || p.end) - Date.parse(p.from)) / 60000)} min{p.finishedAt ? ' · finished' : ''}</div></span>
                    </div>
                  ))}
                </>
              )}
            </>
          );
        })()}

        {/* Time at the machine */}
        {task.kind === 'attention' && inPlay && (pickUp || (running
          ? <AttentionRun running={running} defs={defs} ctl={ctl} />
          : <><Def d={d('start')} /><button onClick={() => begin(task, 'start')} style={wide}>Start the hour</button></>))}

        {/* Assembly: a batch, or step by step */}
        {task.kind === 'assembly' && inPlay && (pickUp || (running
          ? (task.method === 'steps' ? <LapRun running={running} task={task} defs={defs} ctl={ctl} /> : <BatchRun running={running} task={task} defs={defs} ctl={ctl} />)
          : <AssemblyStart task={task} defs={defs} onStart={begin} />))}

        {/* A routine: laps */}
        {task.kind === 'routine' && inPlay && (pickUp || (running
          ? <LapRun running={running} task={task} defs={defs} ctl={ctl} />
          : <RoutineStart task={task} defs={defs} onStart={begin} />))}

        {confirmAbandon && (
          <div style={{ margin: '0 14px 14px', background: '#12161b', border: `1px solid ${AMBER}`, borderRadius: 12, padding: 12, fontSize: 13 }}>
            Throw this recording away? It is not counted, and the measurement stays on your list.
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button disabled={busy} onClick={abandon} style={{ ...smallBtn, color: AMBER, borderColor: AMBER }}>Throw it away</button>
              <button onClick={() => setConfirmAbandon(false)} style={smallBtn}>Keep measuring</button>
            </div>
          </div>
        )}
      </>
    );
  } else {
    const todo = tasks.filter(t => t.status === 'assigned' && !queued(t)), done = tasks.filter(t => t.status === 'done');
    body = (
      <>
        <Head k={today()} h="Yours today" s={tasks.length ? '' : 'Nothing given to you today.'} />
        {active && <button onClick={() => setScreen({ name: 'task', id: active.taskId })} style={{ ...item, borderColor: TEAL, background: 'rgba(20,184,166,0.12)' }}>
          <span style={{ ...ic }}>●</span><span><div style={{ fontWeight: 700 }}>{active.title}</div><div style={{ fontSize: 12, color: DIM }}>running · {clock(Date.now() - Date.parse(active.startedAt))}</div></span>
        </button>}
        {todo.map(t => (
          <button key={t.id} onClick={() => setScreen({ name: 'task', id: t.id })} style={item}>
            <span style={ic}><i className={`fa-solid ${ICON[t.kind] || 'fa-circle'}`} aria-hidden="true" /></span>
            <span style={{ minWidth: 0, textAlign: 'left' }}><div style={{ fontWeight: 700 }}>{t.title}</div><div style={{ fontSize: 12, color: DIM }}>{subOf(t)}</div></span>
            <span style={{ marginLeft: 'auto', fontFamily: 'ui-monospace, monospace', fontSize: 11, color: TEAL }}>{active && active.taskId === t.id ? 'running' : 'start'}</span>
          </button>
        ))}
        {outbox.length > 0 && <div style={sect}>Saved on this phone</div>}
        {outbox.map(x => (
          <div key={x.taskId} style={{ ...item, cursor: 'default', flexWrap: 'wrap' }}>
            <span style={ic}>↑</span>
            <span style={{ minWidth: 0, flex: 1 }}><div style={{ fontWeight: 700 }}>{x.title}</div>
              <div style={{ fontSize: 12, color: x.error ? AMBER : DIM }}>{x.error ? `The app could not take it: ${x.error}` : 'Goes by itself when there is signal'}</div></span>
            {x.error && <button onClick={() => dropQueued(x.taskId)} style={{ ...smallBtn, color: AMBER, borderColor: AMBER }}>Throw it away</button>}
          </div>
        ))}
        {done.length > 0 && <div style={sect}>Done</div>}
        {done.map(t => (
          <div key={t.id} style={{ ...item, opacity: 0.75 }}><span style={ic}>✓</span><span><div style={{ fontWeight: 700 }}>{t.title}</div><div style={{ fontSize: 12, color: GREEN }}>measured</div></span></div>
        ))}
        <PhoneSetup compact />
      </>
    );
  }

  return (
    <div role="dialog" aria-label="Measuring" style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ background: BG, color: INK, width: '100%', maxWidth: 460, height: '100%', overflowY: 'auto', boxShadow: '0 0 40px rgba(0,0,0,0.5)', paddingBottom: 40 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: `1px solid ${LINE}`, position: 'sticky', top: 0, background: BG, zIndex: 1 }}>
          <span style={{ fontWeight: 800, color: TEAL, letterSpacing: '0.04em' }}>MEASURING</span>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', color: DIM, fontSize: 22, cursor: 'pointer' }}>✕</button>
        </div>
        {error && <Box tone={AMBER}>{error}</Box>}
        {active && !active.synced && <div style={{ ...hint, margin: '8px 16px 0' }}>Saved on this phone; sent when there is signal.</div>}
        {body}
      </div>
    </div>
  );
}

function Result({ t, m, onDone }) {
  const r = (m && m.result) || {}, a = (m && m.assumed) || {};
  let lead = 'Measured', big = m ? `${m.value} ${m.unit}` : '', rows = [], note = 'The director sees this and decides whether the app takes it on. Nothing on the board changes by itself.';
  if (m && m.kind === 'mould_change') {
    lead = 'The mould change took'; big = `${r.total_min} min`;
    if (r.off_min != null) rows.push(['Old mould off', `${r.off_min} min`, a.off_min ? `${a.off_min} assumed` : '']);
    rows.push(['New mould on', `${r.on_min} min`, a.on_min ? `${a.on_min} assumed` : '']);
    rows.push(['To good parts', `${r.startup_min} min`, '']);
    if (r.startup_scrap != null) rows.push(['Start-up scrap', `${r.startup_scrap} parts`, '']);
  } else if (m && m.kind === 'warmup') {
    lead = 'Warm-up took'; big = `${m.value} min`; rows.push(['The app assumed', `${a.warmup_min || 30} min`, '']);
  } else if (m && m.kind === 'cycle') {
    lead = 'Cycle time on the display'; big = `${m.value} s`; if (r.planned_s) rows.push(['Planned', `${r.planned_s} s`, '']);
  } else if (m && m.kind === 'packing') {
    const unit = (t && t.assumed && t.assumed.unit) || 'box';
    lead = `One ${unit} of ${r.per_box || ''} took`; big = secs(r.box_s);
    rows.push([`${cap(plural(unit))} timed`, String(r.boxes), '']);
    if (r.pallet_min != null) rows.push([`A whole pallet${r.pallet_min_includes_finish ? ', finished' : ''}`, `${r.pallet_min} min`, '']);
    if (r.pallet_finish_s != null) rows.push(['Wrapping, covering, labelling', secs(r.pallet_finish_s), '']);
    if (r.pallet_change_s != null) rows.push(['Changing the pallet', '+' + secs(r.pallet_change_s), '']);
    if (r.per_1000_min != null) rows.push(['Packing per 1,000 parts', `${r.per_1000_min} min`, '']);
  } else if (m && m.kind === 'purge') {
    lead = 'The purge took'; big = `${m.value} min`;
    if (r.from && r.to) rows.push(['From, to', `${r.from} → ${r.to}`, '']);
  } else if (m && m.kind === 'cleaning') {
    lead = 'Cleaning took'; big = `${m.value} min`; rows.push(['The app assumed', `${a.cleaning_min || 20} min`, '']);
  } else if (m && m.kind === 'attention') {
    lead = 'At the machine'; big = `${r.min_per_hour} min an hour`;
    rows.push(['Visits', `${r.visits} over ${r.span_min} min`, '']);
    rows.push(['Over a shift', `${r.hours_per_shift} h: level ${r.level}`, a.level ? `level ${a.level} set` : 'no level set']);
  } else if (m && (m.kind === 'assembly' || m.kind === 'routine')) {
    lead = m.kind === 'routine' ? 'One takes' : 'Person-time for one'; big = secs(Number(m.value));
    rows.push(['Finished', `${r.count} by ${r.people} ${r.people === 1 ? 'person' : 'people'}`, '']);
    rows.push(['A person an hour', String(Math.round(r.per_hour)), '']);
    if (r.prep_min != null) rows.push(['Getting ready', `${r.prep_min} min`, '']);
    if (r.paused_min) rows.push(['Paused, kept out', `${r.paused_min} min`, '']);
    for (const s of r.steps || []) rows.push([s.name, secs(s.person_s), `${s.share}%`]);
    if (m.kind === 'routine') note = 'The director sees this. Named steps become buttons once they are confirmed.';
  }
  return (
    <>
      <div style={{ textAlign: 'center', padding: '22px 16px 6px' }}>
        <div style={{ color: DIM, fontSize: 13 }}>{lead}</div>
        <div style={{ fontSize: 46, fontWeight: 800, letterSpacing: '-0.02em' }}>{big}</div>
        <div style={{ color: DIM, fontSize: 13 }}>{t ? t.title : ''} · thank you</div>
      </div>
      {rows.length > 0 && (
        <div style={{ margin: '8px 14px', padding: '11px 13px', borderRadius: 12, background: CARD, border: `1px solid ${LINE}`, fontSize: 13 }}>
          {rows.map(([l, v, x]) => <div key={l} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '3px 0' }}><span>{l}</span><span style={{ fontFamily: 'ui-monospace, monospace', textAlign: 'right' }}>{v}{x ? <span style={{ color: MUTED }}> · {x}</span> : null}</span></div>)}
        </div>
      )}
      <div style={{ margin: '6px 14px', padding: '10px 12px', borderLeft: `3px solid ${TEAL}`, background: '#12161b', borderRadius: '0 10px 10px 0', fontSize: 12.5, color: DIM }}>
        {note}
      </div>
      <button onClick={onDone} style={wide}>Done</button>
    </>
  );
}
